import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, catchError, throwError } from 'rxjs'
import { NotificationService } from '../services/notification.service'
import { Message } from '../enums/message.enum'
import * as Sentry from "@sentry/angular";
import { EnvironmentService } from '../services/environment.service'

class HttpError extends Error {
  constructor(title: string, message: string, public details: any) {
    super(title);
    this.name = title;
    this.message = message
  }
}

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptor implements HttpInterceptor {
  notificationService = inject(NotificationService)
  private environmentService = inject(EnvironmentService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skipIntercept = request.headers.has('skip');
    if (skipIntercept) {
      request = request.clone({
        headers: request.headers.delete('skip')
      });
    }
    return next.handle(request).pipe(catchError((err: any) => {
        const errorResponse = err as HttpErrorResponse;

        // Handle cases where HTML is returned instead of expected content type
        if (errorResponse.error instanceof ProgressEvent) {
          // Network error - no response received
          err.error = Message.ErrorMessageNoConnection;
      } else if (typeof errorResponse.error === 'string' && errorResponse.error.trim().startsWith('<!DOCTYPE') || errorResponse.error?.trim().startsWith('<html')) {
          // HTML error page received instead of JSON
          console.warn('Received HTML response instead of JSON:', errorResponse.url);
          err.error = 'Server returned an unexpected response. Please try again.';
        } else if (err?.error && typeof err.error === 'string') {
          // ✅ Safe JSON parsing
          try {
            const parsed = JSON.parse(err.error);
            if (parsed?.ErrorDescription) {
              err.error = parsed.ErrorDescription;
            } else {
              err.error = parsed;
            }
          } catch (e) {
            // Not JSON, leave as-is
            console.warn('Error parsing error response:', e);
          }
        } else if (errorResponse.status === 0) {
          err.error = Message.ErrorMessageNoConnection;
        }

      //Only log errors 500 and above to Sentry
        if (errorResponse.status >= 500 && errorResponse.status !== 0) {
          const error = errorResponse;
          const title = `HTTP Error - ${error.status} - ${error.error}`;
          const message = error.error || error.message || error.statusText || 'Unknown error';

          const sentryError = new HttpError(title, message, {
            status: error.status,
            statusText: error.statusText,
            message,
            url: request.url
          });

          Sentry.captureException(sentryError, {
            tags: {
              http_error: 'true',
              status: errorResponse.status.toString(),
              method: request.method,
              message: message,
              app_version: this.environmentService.env.appVersion,
            },
            extra: {
              requestUrl: request.url,
              requestBody: request.body,
              responseBody: error.error,
              errorObject: error,
            },
          });
        }
        if (!skipIntercept) {
          if (errorResponse.status === 401 || errorResponse.status === 403) {
          this.notificationService.showError(errorResponse.status === 401 ? Message.NotAuthorized : Message.Forbidden, Message.Error);
        }
        else if (errorResponse.status == 400 || errorResponse.status == 0) {
            this.notificationService.showError(err.error, Message.Error);
        }
        else {
            this.notificationService.showError(Message.ErrorMessage, Message.Error);
          }
        }

        // Return the HttpErrorResponse as a thrown error
        return throwError(() => errorResponse);
      })
    );
  }
}